const NAVY = [26, 62, 92];
const ORANGE = [232, 98, 26];
const TEXT = [28, 28, 30];
const MUTED = [108, 108, 112];
const LIGHT_GRAY = [243, 244, 246];

const fmtEuro = (val) => {
  if (typeof val === 'number') {
    return val.toFixed(2).replace('.', ',');
  }
  return String(val).replace('.', ',');
};

export function generateInvoiceNumber(transactionId) {
  const key = `walkin_invoice_${transactionId}`;
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;

  const used = new Set();
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k && k.startsWith('walkin_invoice_')) {
      used.add(sessionStorage.getItem(k));
    }
  }

  let num;
  do {
    num = `WLK-2026-${Math.floor(100000 + Math.random() * 900000)}`;
  } while (used.has(num));

  sessionStorage.setItem(key, num);
  return num;
}

export async function generateInvoice(orderData) {
  const { jsPDF } = await import(/* webpackChunkName: "jspdf" */ 'jspdf');
  const {
    invoiceNumber,
    date,
    shopName,
    email,
    address,
    city,
    vatNumber,
    packageName,
    tokens,
    priceEx,
    vat,
    priceIncl,
    paymentMethod,
    transactionId,
  } = orderData;

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const left = 18;
  const right = pageW - 18;

  // Header
  doc.setTextColor(...NAVY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.text('WalkIn', left, 24);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...TEXT);
  const companyLines = [
    'WalkIn B.V.',
    'Gustav Mahlerplein 2',
    '1082 MA Amsterdam',
    'Nederland',
    'hello@walkin.nl',
    'walkin.nl',
    'BTW: NL000000000B01',
    'KVK: 00000000',
  ];
  let y = 32;
  companyLines.forEach((line) => {
    doc.text(line, left, y);
    y += 4.5;
  });

  doc.setTextColor(...ORANGE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.text('FACTUUR', right, 24, { align: 'right' });

  doc.setFontSize(10);
  doc.setTextColor(...TEXT);
  doc.setFont('helvetica', 'normal');
  const meta = [
    ['Factuurnummer:', invoiceNumber],
    ['Factuurdatum:', date],
    ['Vervaldatum:', `${date} (direct)`],
    ['Betaalstatus:', '✓ BETAALD'],
  ];
  let metaY = 34;
  meta.forEach(([label, value]) => {
    doc.setFont('helvetica', 'normal');
    doc.text(label, right - 50, metaY);
    doc.setFont('helvetica', 'bold');
    if (label === 'Betaalstatus:') doc.setTextColor(...ORANGE);
    doc.text(value, right, metaY, { align: 'right' });
    doc.setTextColor(...TEXT);
    metaY += 5;
  });

  doc.setDrawColor(...ORANGE);
  doc.setLineWidth(0.6);
  doc.line(left, 76, right, 76);

  let billY = 86;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...NAVY);
  doc.text('Factuur aan:', left, billY);
  billY += 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...TEXT);
  doc.text(shopName || '—', left, billY);
  billY += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  if (email) { doc.text(email, left, billY); billY += 4.5; }
  if (address) { doc.text(address, left, billY); billY += 4.5; }
  if (city) { doc.text(city, left, billY); billY += 4.5; }
  doc.text(`BTW nummer: ${vatNumber || 'Niet opgegeven'}`, left, billY);
  billY += 4.5;

  const tableY = Math.max(billY + 8, 130);
  const colX = { desc: left, qty: 110, price: 138, total: right };

  doc.setFillColor(...NAVY);
  doc.rect(left, tableY - 6, right - left, 9, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('OMSCHRIJVING', colX.desc + 2, tableY);
  doc.text('AANTAL', colX.qty, tableY);
  doc.text('PRIJS EX BTW', colX.price, tableY);
  doc.text('TOTAAL', colX.total - 2, tableY, { align: 'right' });

  let rowY = tableY + 10;
  doc.setTextColor(...TEXT);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`${packageName} deal tokens`, colX.desc + 2, rowY);
  doc.text(String(tokens), colX.qty, rowY);
  doc.text(`€ ${fmtEuro(priceEx)}`, colX.price, rowY);
  doc.text(`€ ${fmtEuro(priceEx)}`, colX.total - 2, rowY, { align: 'right' });

  doc.setDrawColor(229, 229, 234);
  doc.setLineWidth(0.2);
  doc.line(left, rowY + 4, right, rowY + 4);

  let totY = rowY + 14;
  const totLabelX = right - 60;
  doc.setFontSize(10);
  doc.setTextColor(...TEXT);
  doc.setFont('helvetica', 'normal');

  doc.text('Subtotaal:', totLabelX, totY);
  doc.text(`€ ${fmtEuro(priceEx)}`, right, totY, { align: 'right' });
  totY += 6;

  doc.text('BTW 21%:', totLabelX, totY);
  doc.text(`€ ${fmtEuro(vat)}`, right, totY, { align: 'right' });
  totY += 4;

  doc.setDrawColor(...TEXT);
  doc.setLineWidth(0.4);
  doc.line(totLabelX, totY, right, totY);
  totY += 7;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...ORANGE);
  doc.text('TOTAAL INCL. BTW:', totLabelX, totY);
  doc.text(`€ ${fmtEuro(priceIncl)}`, right, totY, { align: 'right' });

  const boxY = totY + 14;
  const boxH = 26;
  doc.setFillColor(...LIGHT_GRAY);
  doc.roundedRect(left, boxY, right - left, boxH, 2, 2, 'F');

  doc.setTextColor(...TEXT);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Betaalgegevens', left + 4, boxY + 7);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  let pY = boxY + 13;
  doc.text(`Betaalwijze: ${paymentMethod || 'iDEAL'}`, left + 4, pY);
  doc.text(`Betaaldatum: ${date}`, right - 4, pY, { align: 'right' });
  pY += 5;
  doc.text(`Transactienummer: ${transactionId || '—'}`, left + 4, pY);
  doc.setTextColor(...ORANGE);
  doc.setFont('helvetica', 'bold');
  doc.text('Status: BETAALD ✓', right - 4, pY, { align: 'right' });

  const footerY = pageH - 22;
  doc.setTextColor(...TEXT);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Bedankt voor uw aankoop bij WalkIn!', pageW / 2, footerY, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text('Bij vragen: hello@walkin.nl  |  walkin.nl', pageW / 2, footerY + 5, { align: 'center' });
  doc.text('WalkIn B.V. · Amsterdam · KVK: 00000000 · BTW: NL000000000B01', pageW / 2, footerY + 10, { align: 'center' });

  doc.setDrawColor(...ORANGE);
  doc.setLineWidth(0.8);
  doc.line(left, pageH - 8, right, pageH - 8);

  doc.save(`WalkIn-Factuur-${invoiceNumber}.pdf`);
}
